"use client";

import { fetchImpromptuApi } from "@/lib/impromptu/client-api";
import {
  IMPROMPTU_MAX_DURATION_SECONDS,
  IMPROMPTU_MAX_PROMPT_CHARACTERS,
  IMPROMPTU_MIN_PROMPT_CHARACTERS,
  IMPROMPTU_PREP_SECONDS,
  IMPROMPTU_PROMPT_CATEGORIES,
  IMPROMPTU_TARGET_SECONDS,
  type ImpromptuLanguageCode,
  type ImpromptuLearnerProfile,
  type ImpromptuPrompt,
  type ImpromptuPromptCategory,
  type ImpromptuPromptSelection,
  isValidImpromptuPromptText,
  normalizeImpromptuPromptText,
  toImpromptuPromptSelection,
} from "@/lib/impromptu/config";
import { IMPROMPTU_COPY } from "@/lib/impromptu/copy";
import { LibraryBig, LoaderCircle, Mic, RefreshCw, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

const COPY = IMPROMPTU_COPY.promptStudio;

type PromptMode = "library" | "generate" | "write";
type CategoryFilter = "all" | ImpromptuPromptCategory;

type PromptStudioProps = {
  prompt: ImpromptuPromptSelection;
  prompts: ReadonlyArray<ImpromptuPrompt>;
  languageCode: ImpromptuLanguageCode;
  learnerProfile: ImpromptuLearnerProfile;
  onPromptChange: (prompt: ImpromptuPromptSelection) => void;
  onStart: () => void;
  onBrowsePrompts?: () => void;
  surface?: "landing" | "session";
};

function chooseUnseenPrompt(
  prompts: ReadonlyArray<ImpromptuPrompt>,
  currentId: string,
  seenPromptIds: Set<string>
): ImpromptuPrompt {
  const unseen = prompts.filter(
    (candidate) => candidate.id !== currentId && !seenPromptIds.has(candidate.id)
  );
  const alternatives = prompts.filter((candidate) => candidate.id !== currentId);
  const pool = unseen.length > 0 ? unseen : alternatives.length > 0 ? alternatives : prompts;
  if (unseen.length === 0) seenPromptIds.clear();
  const selected = pool[Math.floor(Math.random() * pool.length)];
  seenPromptIds.add(selected.id);
  return selected;
}

export function PromptStudio({
  prompt,
  prompts,
  languageCode,
  learnerProfile,
  onPromptChange,
  onStart,
  onBrowsePrompts,
  surface = "session",
}: PromptStudioProps) {
  const [mode, setMode] = useState<PromptMode>("library");
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [customText, setCustomText] = useState("");
  const [topic, setTopic] = useState("");
  const [isShuffling, setIsShuffling] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shuffleTimerRef = useRef<number | null>(null);
  const pendingStartPromptIdRef = useRef<string | null>(null);
  const seenPromptIdsRef = useRef(new Set<string>([prompt.id]));

  const filteredPrompts = useMemo(
    () =>
      category === "all" ? prompts : prompts.filter((candidate) => candidate.category === category),
    [category, prompts]
  );
  const promptNumber = Math.max(
    1,
    prompts.findIndex((candidate) => candidate.id === prompt.id) + 1
  );
  const categoryLabel =
    IMPROMPTU_PROMPT_CATEGORIES.find((item) => item.value === prompt.category)?.label ||
    COPY.ownTopicLabel;

  useEffect(
    () => () => {
      if (shuffleTimerRef.current) window.clearTimeout(shuffleTimerRef.current);
    },
    []
  );

  useEffect(() => {
    setCategory("all");
    setMode("library");
    setError(null);
    pendingStartPromptIdRef.current = null;
    seenPromptIdsRef.current = new Set();
  }, [languageCode]);

  useEffect(() => {
    if (pendingStartPromptIdRef.current !== prompt.id) return;
    pendingStartPromptIdRef.current = null;
    onStart();
  }, [onStart, prompt.id]);

  const shuffle = () => {
    if (isShuffling || filteredPrompts.length === 0) return;
    setError(null);
    setIsShuffling(true);
    shuffleTimerRef.current = window.setTimeout(() => {
      const selected = chooseUnseenPrompt(filteredPrompts, prompt.id, seenPromptIdsRef.current);
      onPromptChange(toImpromptuPromptSelection(selected));
      setIsShuffling(false);
      shuffleTimerRef.current = null;
    }, 480);
  };

  const chooseCategory = (nextCategory: CategoryFilter) => {
    setCategory(nextCategory);
    if (mode !== "library" || nextCategory === "all") return;
    const categoryPrompts = prompts.filter((candidate) => candidate.category === nextCategory);
    if (categoryPrompts.length === 0) return;
    const selected = chooseUnseenPrompt(categoryPrompts, prompt.id, seenPromptIdsRef.current);
    onPromptChange(toImpromptuPromptSelection(selected));
  };

  const start = () => {
    if (mode !== "write") {
      onStart();
      return;
    }

    const text = normalizeImpromptuPromptText(customText);
    if (!isValidImpromptuPromptText(text)) {
      setError(COPY.lengthError(IMPROMPTU_MIN_PROMPT_CHARACTERS, IMPROMPTU_MAX_PROMPT_CHARACTERS));
      return;
    }
    setError(null);
    const nextPrompt: ImpromptuPromptSelection = {
      id: `custom-${crypto.randomUUID()}`,
      source: "custom",
      category: category === "all" ? "opinion" : category,
      languageCode,
      text,
      preparationSeconds: IMPROMPTU_PREP_SECONDS,
      targetSeconds: IMPROMPTU_TARGET_SECONDS,
    };
    pendingStartPromptIdRef.current = nextPrompt.id;
    onPromptChange(nextPrompt);
  };

  const generatePrompt = async () => {
    if (isGenerating) return;
    setError(null);
    setIsGenerating(true);
    try {
      const response = await fetchImpromptuApi("/api/impromptu/generate-prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          languageCode,
          learnerProfile,
          category: category === "all" ? "opinion" : category,
          topic,
        }),
      });
      const payload: unknown = await response.json();
      if (!response.ok) {
        const message =
          payload &&
          typeof payload === "object" &&
          "error" in payload &&
          typeof payload.error === "string"
            ? payload.error
            : COPY.generateFailed;
        throw new Error(message);
      }
      if (
        !payload ||
        typeof payload !== "object" ||
        !("id" in payload) ||
        typeof payload.id !== "string" ||
        !("text" in payload) ||
        typeof payload.text !== "string" ||
        !("category" in payload) ||
        typeof payload.category !== "string" ||
        !IMPROMPTU_PROMPT_CATEGORIES.some((item) => item.value === payload.category) ||
        !isValidImpromptuPromptText(payload.text)
      ) {
        throw new Error(COPY.generateInvalid);
      }
      onPromptChange({
        id: payload.id,
        source: "generated",
        category: payload.category as ImpromptuPromptCategory,
        languageCode,
        text: normalizeImpromptuPromptText(payload.text),
        preparationSeconds: IMPROMPTU_PREP_SECONDS,
        targetSeconds: IMPROMPTU_TARGET_SECONDS,
      });
      setMode("library");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : COPY.generateFailed);
    } finally {
      setIsGenerating(false);
    }
  };

  const isLanding = surface === "landing";
  const divider = isLanding ? "border-[#D1D1D6]" : "border-white/10";
  const heading = isLanding ? "text-[#1C1C1E]" : "text-white";
  const body = isLanding ? "text-[#48484A]" : "text-[#AEAEB2]";
  const muted = isLanding ? "text-[#6C6C70]" : "text-[#8E8E93]";
  const input = isLanding
    ? "border-[#C7C7CC] bg-white text-[#1C1C1E] placeholder:text-[#8E8E93]"
    : "border-white/15 bg-[#16171C] text-white placeholder:text-[#8E8E93]";

  return (
    <section
      className={`min-w-0 border-y ${divider} ${isLanding ? "text-[#1C1C1E]" : "text-white"}`}
      aria-labelledby="impromptu-prompt-studio-title"
    >
      <div className={`flex flex-wrap items-end justify-between gap-3 border-b py-4 ${divider}`}>
        <div>
          <h2 id="impromptu-prompt-studio-title" className={`text-xl font-extrabold ${heading}`}>
            {COPY.panelLabel}
          </h2>
          <p className={`mt-1 text-sm ${muted}`}>{COPY.topicCount(prompts.length)}</p>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2" aria-label={COPY.sourceGroupLabel}>
          {(
            [
              ["library", COPY.sourceLibrary],
              ["generate", COPY.sourceGenerate],
              ["write", COPY.sourceWrite],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setMode(value);
                setError(null);
              }}
              className={`min-h-11 border-b-2 px-0.5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] ${
                mode === value
                  ? "border-[#FD954F] text-[#A8431F]"
                  : `border-transparent ${muted} hover:text-[#A8431F]`
              }`}
              aria-pressed={mode === value}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {mode === "library" && (
        <>
          <div
            className={`flex gap-x-5 gap-y-1 overflow-x-auto border-b py-3 lg:justify-between lg:gap-x-0 lg:overflow-visible ${divider}`}
            aria-label={COPY.categoryGroupLabel}
          >
            <button
              type="button"
              onClick={() => chooseCategory("all")}
              className={`min-h-10 shrink-0 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] ${
                category === "all" ? "text-[#A8431F]" : `${muted} hover:text-[#A8431F]`
              }`}
            >
              {COPY.categoryAny}
            </button>
            {IMPROMPTU_PROMPT_CATEGORIES.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => chooseCategory(item.value)}
                className={`min-h-10 shrink-0 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] ${
                  category === item.value ? "text-[#A8431F]" : `${muted} hover:text-[#A8431F]`
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="py-7 sm:py-9">
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#A8431F]">
                {categoryLabel}
              </p>
              <p className={`text-xs font-semibold tabular-nums ${muted}`}>
                {prompt.source === "curated"
                  ? `${promptNumber} of ${prompts.length}`
                  : COPY.madeForYou}
              </p>
            </div>
            <p
              className={`mt-5 max-w-[28ch] text-[1.75rem] font-extrabold leading-[1.16] transition-all duration-300 motion-reduce:transition-none sm:text-[2.35rem] ${heading} ${
                isShuffling ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"
              }`}
              aria-live="polite"
            >
              {prompt.text}
            </p>
            <p className={`mt-5 text-sm font-semibold ${muted}`}>
              {COPY.speakSeconds(IMPROMPTU_MAX_DURATION_SECONDS)}
            </p>
          </div>

          <div
            className={`flex flex-col gap-3 border-t py-4 sm:flex-row sm:items-center ${divider}`}
          >
            <button
              type="button"
              onClick={shuffle}
              disabled={isShuffling}
              className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border px-5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] disabled:cursor-wait ${
                isLanding
                  ? "border-[#C7C7CC] bg-white text-[#1C1C1E] hover:border-[#A8431F]"
                  : "border-white/15 text-white hover:bg-white/[0.06]"
              }`}
              aria-label={isShuffling ? COPY.shuffleBusyLabel : COPY.shuffleLabel}
            >
              <RefreshCw
                className={`h-4 w-4 ${isShuffling ? "motion-safe:animate-spin" : ""}`}
                aria-hidden="true"
              />
              {isShuffling ? COPY.shuffleBusy : COPY.shuffle}
            </button>
            {onBrowsePrompts && (
              <button
                type="button"
                onClick={onBrowsePrompts}
                className={`inline-flex min-h-12 items-center justify-center gap-2 px-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] ${body} hover:text-[#A8431F]`}
              >
                <LibraryBig className="h-4 w-4" aria-hidden="true" />
                {COPY.browseAll(prompts.length)}
              </button>
            )}
          </div>
        </>
      )}

      {mode === "generate" && (
        <div className="py-7 sm:py-9">
          <Sparkles className="h-6 w-6 text-[#A8431F]" aria-hidden="true" />
          <h3 className={`mt-4 text-2xl font-extrabold leading-[1.18] ${heading}`}>
            {COPY.generateHeading}
          </h3>
          <p className={`mt-2 max-w-md text-sm leading-6 ${body}`}>{COPY.generateBody}</p>
          <label className={`mt-6 block text-sm font-bold ${heading}`} htmlFor="impromptu-topic">
            {COPY.generateSubjectLabel}{" "}
            <span className={`font-medium ${muted}`}>{COPY.generateSubjectOptional}</span>
          </label>
          <input
            id="impromptu-topic"
            value={topic}
            maxLength={IMPROMPTU_MAX_PROMPT_CHARACTERS}
            onChange={(event) => setTopic(event.target.value)}
            placeholder={COPY.generateSubjectPlaceholder}
            className={`mt-2 min-h-12 w-full rounded-lg border px-4 text-sm outline-none transition-colors focus:border-[#FD954F] ${input}`}
          />
          <button
            type="button"
            onClick={() => void generatePrompt()}
            disabled={isGenerating}
            className={`mt-4 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border px-5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] disabled:cursor-wait disabled:opacity-60 ${
              isLanding
                ? "border-[#C7C7CC] bg-white text-[#1C1C1E] hover:border-[#A8431F]"
                : "border-white/15 text-white hover:bg-white/[0.06]"
            }`}
          >
            {isGenerating && (
              <LoaderCircle className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
            )}
            {isGenerating ? COPY.generateBusy : COPY.generateAction}
          </button>
        </div>
      )}

      {mode === "write" && (
        <div className="py-7 sm:py-9">
          <h3 className={`text-2xl font-extrabold leading-[1.18] ${heading}`}>
            {COPY.writeHeading}
          </h3>
          <p className={`mt-2 max-w-md text-sm leading-6 ${body}`}>{COPY.writeBody}</p>
          <label
            className={`mt-6 block text-sm font-bold ${heading}`}
            htmlFor="impromptu-custom-prompt"
          >
            {COPY.writeLabel}
          </label>
          <textarea
            id="impromptu-custom-prompt"
            value={customText}
            maxLength={IMPROMPTU_MAX_PROMPT_CHARACTERS}
            rows={4}
            onChange={(event) => {
              setCustomText(event.target.value);
              if (error) setError(null);
            }}
            placeholder={COPY.writePlaceholder}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "impromptu-custom-prompt-error" : undefined}
            className={`mt-2 w-full resize-none rounded-lg border px-4 py-3 text-sm leading-6 outline-none transition-colors focus:border-[#FD954F] ${input}`}
          />
          <p className={`mt-3 text-xs tabular-nums ${muted}`}>
            {customText.length}/{IMPROMPTU_MAX_PROMPT_CHARACTERS}
          </p>
          {error && (
            <p
              id="impromptu-custom-prompt-error"
              className="mt-3 text-sm leading-6 text-[#A8431F]"
              role="alert"
            >
              {error}
            </p>
          )}
        </div>
      )}

      {error && mode !== "write" && (
        <p className={`border-t py-3 text-sm leading-6 text-[#A8431F] ${divider}`} role="alert">
          {error}
        </p>
      )}

      <div className={`border-t py-4 ${divider}`}>
        <button
          type="button"
          onClick={start}
          className="inline-flex min-h-14 w-full items-center justify-center gap-3 rounded-lg bg-[#FD954F] px-6 text-base font-bold text-[#1C1C1E] transition-colors hover:bg-[#F28747] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] focus-visible:ring-offset-2"
        >
          <Mic className="h-5 w-5" aria-hidden="true" />
          {COPY.start}
        </button>
      </div>
    </section>
  );
}
